import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { map } from 'rxjs';
import { TenantService } from '../core/tenant.service';
import { resolveAllowedRoutes } from '../core/feature-registry';

/**
 * FeatureGuard — Angular 17+ functional guard (CanActivateFn).
 *
 * Checks whether the current route is allowed for the tenant's business type
 * and active modules. Uses the feature configuration registry as the single
 * source of truth for route access.
 *
 * - Redirects to /registration if no tenant data is available.
 * - Redirects silently to /dashboard if the route is not in the allowed set.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */
export const featureGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const tenantService = inject(TenantService);
  const router = inject(Router);

  return tenantService.loadTenant().pipe(
    map((tenant) => {
      if (!tenant) {
        return router.createUrlTree(['/registration']);
      }

      const allowedRoutes = resolveAllowedRoutes(
        tenant.business_type,
        tenant.active_modules ?? [],
        {
          quotations_enabled: tenant.quotations_enabled ?? true,
          work_orders_enabled: tenant.work_orders_enabled ?? true,
          invoicing_enabled: tenant.invoicing_enabled ?? true,
        },
        tenant.billing_tier
      );

      const baseRoute = '/' + (route.url[0]?.path ?? '');

      if (allowedRoutes.has(baseRoute)) {
        return true;
      }

      // Silent redirect to dashboard — no error message displayed
      return router.createUrlTree(['/dashboard']);
    })
  );
};
